import { appHref } from '../globals.js';
import { getTheme, setTheme, type ThemeChoice } from '../theme.js';

/**
 * The account menu, and the appearance control inside it.
 *
 * It lived on the launcher alone, which made the theme a setting you could
 * only reach by leaving whatever you were doing and going back to the apps
 * list — for a control whose whole purpose is to be changed when the light
 * in the room changes, usually in the middle of something. Every app's shell
 * carries it now, from one definition, so the three copies cannot drift.
 *
 * The theme itself was already global: it is applied once in `main.ts` and
 * stored per user per device, so the apps never disagreed about it. Only the
 * way in was missing.
 */

const LABELS: Record<ThemeChoice, string> = {
  system: 'Match the system',
  light: 'Light',
  dark: 'Dark',
};

export interface AccountMenuOptions {
  /** Shown when this menu's app can sign out; the launcher owns that flow. */
  onSignOut?: () => void;
}

export function accountMenuHtml(opts: { signOut?: boolean } = {}): string {
  return `
    <div class="dropdown" data-account-menu>
      <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button"
        data-bs-toggle="dropdown" aria-label="Account and appearance">Account</button>
      <ul class="dropdown-menu dropdown-menu-end">
        <li><a class="dropdown-item" href="${appHref('/change-password')}">Change password</a></li>
        <li><hr class="dropdown-divider"></li>
        <!-- "On this device" is not a disclaimer, it is the feature: how dark
             a screen should be is a fact about the room it is in. -->
        <li><h6 class="dropdown-header">Appearance · this device</h6></li>
        ${(['system', 'light', 'dark'] as const).map((choice) => `
          <li><button class="dropdown-item" type="button" data-theme="${choice}">
            <span data-theme-tick="${choice}" aria-hidden="true">&nbsp;</span>
            ${LABELS[choice]}</button></li>`).join('')}
        ${opts.signOut === false ? '' : `
        <li><hr class="dropdown-divider"></li>
        <li><button class="dropdown-item" type="button" data-account-signout>Sign out</button></li>`}
      </ul>
    </div>`;
}

/** Wire the menu inside `root`. Safe to call on a root that has none. */
export function wireAccountMenu(
  root: HTMLElement, userId: string, opts: AccountMenuOptions = {},
): void {
  const paint = (): void => {
    const current = getTheme(userId);
    for (const tick of root.querySelectorAll<HTMLElement>('[data-theme-tick]')) {
      // A tick, not a highlight: the menu is a list of three and which one is
      // on must be readable without colour (invariant #11).
      tick.textContent = tick.dataset['themeTick'] === current ? '✓' : ' ';
    }
  };
  for (const btn of root.querySelectorAll<HTMLButtonElement>('[data-theme]')) {
    btn.addEventListener('click', () => {
      setTheme(userId, btn.dataset['theme'] as ThemeChoice);
      paint();
    });
  }
  if (opts.onSignOut) {
    root.querySelector<HTMLButtonElement>('[data-account-signout]')
      ?.addEventListener('click', opts.onSignOut);
  }
  paint();
}
